import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  Button,
  Input,
  Label,
  Modal,
  SearchBox,
  Spinner,
  useToast,
} from "@toss-ads/ui";
import {
  createPlatform,
  deletePlatform,
  listPlatforms,
  updatePlatform,
} from "../../lib/api";
import type { Platform } from "../../lib/types";

const SLUG_RE = /^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/;

export function SecretsPlatformsListPage() {
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Platform | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin", "platforms"],
    queryFn: listPlatforms,
  });

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = data ?? [];
    if (!q) return rows;
    return rows.filter((p) => `${p.name} ${p.slug}`.toLowerCase().includes(q));
  }, [data, search]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h1 className="text-[18px] font-semibold tracking-tight">매체 관리</h1>
        <Button size="md" onClick={() => setOpen(true)}>
          + 매체 추가
        </Button>
      </div>

      <p className="text-[12px] text-ink-subtle mb-3">
        벤더가 리드에 보내는 <span className="font-mono">channel</span> 값(=slug)이
        여기 매체와 일치하면 리드 목록·통계에 그 매체로 표시됩니다. (기존 리드
        소급 반영은 별도 백필 필요)
      </p>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <SearchBox value={search} onChange={setSearch} placeholder="매체명 / slug 검색" />
      </div>

      <div className="rounded-lg border bg-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead className="bg-surface-muted">
            <tr>
              <Th width={160}>slug (channel)</Th>
              <Th>매체명</Th>
              <Th width={60}>로고</Th>
              <Th width={72}>수정</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={4} className="py-16">
                  <div className="flex justify-center text-ink-subtle">
                    <Spinner size={20} />
                  </div>
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={4} className="py-12 text-center">
                  <p className="text-[13px] text-danger mb-2">
                    {error instanceof ApiError
                      ? error.message
                      : "불러오지 못했습니다."}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => void refetch()}>
                    다시 시도
                  </Button>
                </td>
              </tr>
            )}
            {!isLoading && !isError && filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="py-12 text-center text-[13px] text-ink-subtle">
                  매체가 없습니다.
                </td>
              </tr>
            )}
            {filtered.map((p) => (
              <tr
                key={p.id}
                className="border-t border-border hover:bg-surface-muted"
              >
                <Td className="font-mono text-ink">{p.slug}</Td>
                <Td className="font-medium text-ink">{p.name}</Td>
                <Td>
                  {p.logoUrl ? (
                    <img
                      src={p.logoUrl}
                      alt=""
                      className="w-5 h-5 rounded object-contain"
                    />
                  ) : (
                    <span className="text-ink-subtle">—</span>
                  )}
                </Td>
                <Td>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(p)}
                  >
                    수정
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreatePlatformModal open={open} onClose={() => setOpen(false)} />
      <EditPlatformModal platform={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function CreatePlatformModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [slug, setSlug] = React.useState("");
  const [name, setName] = React.useState("");
  const [logoUrl, setLogoUrl] = React.useState("");
  const [err, setErr] = React.useState<{ slug?: string; name?: string }>({});

  React.useEffect(() => {
    if (open) {
      setSlug("");
      setName("");
      setLogoUrl("");
      setErr({});
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      createPlatform({
        slug: slug.trim(),
        name: name.trim(),
        logoUrl: logoUrl.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platforms"] });
      toast.success("매체가 추가되었습니다");
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setErr((p) => ({ ...p, slug: "이미 있는 slug 입니다" }));
      } else {
        toast.error(e instanceof ApiError ? e.message : "추가 실패");
      }
    },
  });

  function submit() {
    const next: typeof err = {};
    if (!SLUG_RE.test(slug.trim())) next.slug = "소문자/숫자/-/_ (예: wonja)";
    if (!name.trim()) next.name = "매체명을 입력하세요";
    setErr(next);
    if (Object.keys(next).length === 0) create.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="매체 추가"
      description="벤더가 보낼 channel 값(slug)과 표시 이름을 정하세요."
    >
      <div className="mb-4">
        <Label htmlFor="pf-slug">slug (channel 값)</Label>
        <Input
          id="pf-slug"
          className="font-mono"
          placeholder="wonja"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          error={!!err.slug}
        />
        {err.slug && <p className="text-[11px] text-danger mt-1">{err.slug}</p>}
      </div>
      <div className="mb-4">
        <Label htmlFor="pf-name">매체명</Label>
        <Input
          id="pf-name"
          placeholder="원자력신문"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!err.name}
        />
        {err.name && <p className="text-[11px] text-danger mt-1">{err.name}</p>}
      </div>
      <div className="mb-5">
        <Label htmlFor="pf-logo">로고 URL (선택)</Label>
        <div className="flex items-center gap-2">
          <Input
            id="pf-logo"
            className="font-mono text-[12px]"
            placeholder="/platforms/wonja.png 또는 https://…"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
          />
          {logoUrl.trim() && (
            <img
              src={logoUrl.trim()}
              alt=""
              className="w-6 h-6 rounded object-contain border border-border shrink-0"
            />
          )}
        </div>
        <p className="text-[11px] text-ink-subtle mt-1">
          이미지 경로/URL. 비우면 로고 없이(머리글자) 표시됩니다.
        </p>
      </div>
      <div className="flex justify-end gap-2 pt-4 border-t border-border">
        <Button variant="text" size="md" onClick={onClose}>
          취소
        </Button>
        <Button size="md" loading={create.isPending} onClick={submit}>
          추가
        </Button>
      </div>
    </Modal>
  );
}

function EditPlatformModal({
  platform,
  onClose,
}: {
  platform: Platform | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = React.useState("");
  const [logoUrl, setLogoUrl] = React.useState("");
  const [confirmDel, setConfirmDel] = React.useState(false);

  React.useEffect(() => {
    if (platform) {
      setName(platform.name);
      setLogoUrl(platform.logoUrl ?? "");
      setConfirmDel(false);
    }
  }, [platform]);

  const save = useMutation({
    mutationFn: () =>
      updatePlatform(platform!.id, {
        name: name.trim(),
        logoUrl: logoUrl.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platforms"] });
      toast.success("저장되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "저장 실패"),
  });

  const del = useMutation({
    mutationFn: () => deletePlatform(platform!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platforms"] });
      toast.success("삭제되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "삭제 실패"),
  });

  return (
    <Modal
      open={!!platform}
      onClose={onClose}
      title="매체 수정"
      description={platform ? `slug: ${platform.slug} (변경 불가)` : ""}
    >
      <div className="mb-4">
        <Label htmlFor="pf-ename">매체명</Label>
        <Input
          id="pf-ename"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="mb-5">
        <Label htmlFor="pf-elogo">로고 URL</Label>
        <div className="flex items-center gap-2">
          <Input
            id="pf-elogo"
            className="font-mono text-[12px]"
            placeholder="/platforms/wonja.png 또는 https://…"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
          />
          {logoUrl.trim() && (
            <img
              src={logoUrl.trim()}
              alt=""
              className="w-6 h-6 rounded object-contain border border-border shrink-0"
            />
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 pt-4 border-t border-border">
        {confirmDel ? (
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-danger">정말 삭제?</span>
            <Button
              size="sm"
              variant="danger"
              loading={del.isPending}
              onClick={() => del.mutate()}
            >
              삭제 확정
            </Button>
            <Button
              size="sm"
              variant="text"
              onClick={() => setConfirmDel(false)}
            >
              취소
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="text"
            className="text-danger"
            onClick={() => setConfirmDel(true)}
          >
            삭제
          </Button>
        )}
        <div className="flex gap-2">
          <Button variant="text" size="md" onClick={onClose}>
            닫기
          </Button>
          <Button
            size="md"
            loading={save.isPending}
            onClick={() => save.mutate()}
            disabled={!name.trim()}
          >
            저장
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Th({ children, width }: { children: React.ReactNode; width?: number }) {
  return (
    <th
      style={{ width }}
      className="px-2.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle border-b border-border whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-2.5 py-3 align-middle ${className ?? ""}`}>{children}</td>;
}
